import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateModule } from "@ngx-translate/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { ApplicationDataService } from "../../../core/services/application-data.service";
import { AlertDialogComponent } from "../../../shared/alert-dialog/alert-dialog.component";
import { CommonModule, DatePipe, NgClass } from "@angular/common";
import { TokenStorageService } from "../../../core/services/token-storage.service";
import { SpinnerService } from "../../../core/services/spinner.service";
import { AttachmentPreviewDialogComponent } from "../complaint-edit/attachment-preview-dialog/attachment-preview-dialog.component";
import { ComplaintPdfService } from "../complaint-pdf.service";

interface Complaint {
  complaintId?: string;
  complaintNo?: string;
  complainantTitle?: string;
  complainantName?: string;
  cnic?: string;
  cellPhoneNo?: string;
  passportNo?: string;
  landLinePhoneNo?: string;
  address?: string;
  complaintCategoryName?: string;
  complaintSubcategoryName?: string;
  divisionName?: string;
  districtName?: string;
  tehsilName?: string;
  tehsilId?: string;
  mouzaId?: string;
  mouzaName?: string;
  daysRequired?: string;
  workCode?: string;
  complaintSource?: string;
  legalFramework?: string;
  helplineIdentifier?: string;
  assignTo?: string;
  complaintDocument?: any[];
  complaintDetails?: string;
  priority?: string;
  severity?: string;
  gender?: string;
  email?: string;
  assignToGroup?: string;
  complaintAddress?: string;
  currentProcess?: {
    complaintProcessId?: string;
    name?: string;
    successorProcessId?: any[];
  };
  isAnonymous?: boolean;
  expectedResolutionDate?: string;
  segmentId?: number;
  otherForum?: string;
  otherSource?: string;
  divisionId?: string;
  districtId?: string;
  assignToName?: string;
  assignToGroupName?: string;

}

interface TrackingItem {
  createdByName?: string;
  createdAt?: string;
  comment?: string;
  statusChangedName?: string;
  priorityChanged?: string;
  severityChanged?: string;
  documentName?: string;
  isRemove?: boolean | null;
  complaintEmailMessage?: string;
  complaintSMSMessage?: string;
  assignedToName?: string;
  assignedToGroupName?: string;
}


@Component({
  selector: "app-complaint-view",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    DatePipe,
    NgClass,
  ],
  templateUrl: "./complaint-view.component.html",
  styleUrl: "./complaint-view.component.css",
})
export class ComplaintViewComponent implements OnInit, OnDestroy {
  userId: string | null = null;
  complaintId: string | null = null;
  isVerifying: boolean = true;
  isError: boolean = false;
  userRole: string = '';
  username: string = '';
  complaint?: Complaint;
  trackingList: TrackingItem[] = [];
  documentList: any[] = [];
  canSeeInfo: boolean = false;
  canViewAnonymousInfo: boolean = false;
  collapsed: { [key: string]: boolean } = {};

  toggleCard(key: string): void {
    this.collapsed[key] = !this.collapsed[key];
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appDataService: ApplicationDataService,
    public tokenService: TokenStorageService,
    private dialog: MatDialog,
    private spinnerService: SpinnerService,
    private cdr: ChangeDetectorRef,
    private pdfService: ComplaintPdfService
  ) { }

  ngOnInit() {
    this.spinnerService.suppress();

    this.route.paramMap.subscribe((params) => {
      this.complaintId = params.get("complaintId");
    });
    this.canSeeInfo = this.tokenService.getCanseeInfo();
    this.canViewAnonymousInfo = this.tokenService.getCanViewAnonymousInfo();
    this.username = this.tokenService.getEmail() || '';
    this.loadUserid();

    if (this.complaintId) {
      this.appDataService.getComplaintById(this.complaintId).subscribe({
        next: (res: any) => {
          this.complaint = res?.data;

          const authStartTime = Date.now();
          const minDelay = 2000;

          const finishVerification = (authorized: boolean) => {
            const elapsed = Date.now() - authStartTime;
            const remaining = Math.max(0, minDelay - elapsed);
            setTimeout(() => {
              if (authorized) {
                this.isVerifying = false;
              } else {
                this.router.navigate(['/complaint']);
              }
            }, remaining);
          };

          if (this.userRole === 'ADMIN') {
            finishVerification(true);
          } else if (this.complaint?.assignToName === this.username) {
            finishVerification(true);
          } else if (this.complaint?.assignToGroup) {
            this.appDataService.getGroupById(Number(this.complaint.assignToGroup)).subscribe({
              next: (groupRes: any) => {
                const groupData = groupRes?.data;
                const isUserInGroup = groupData?.users?.some(
                  (user: any) => user.userName === this.username
                );
                finishVerification(!!isUserInGroup);
              },
              error: () => {
                finishVerification(false);
              }
            });
          } else {
            finishVerification(false);
            return;
          }

          this.documentList = res?.data?.complaintDocument || [];

        },
        error: () => {
          this.isVerifying = false;
          this.isError = true;
        }
      });

      this.appDataService.getTrckingByComplaintId(this.complaintId).subscribe({
        next: (res: any) => {
          this.trackingList = res?.data || [];
        },
      });
    }

  }

  ngOnDestroy(): void {
    this.spinnerService.unsuppress();
  }

  loadUserid(): void {
    const permissions = localStorage.getItem("permissions");
    if (permissions) {
      try {
        const parsedPermissions = JSON.parse(permissions);
        const getUserId = parsedPermissions.USER;
        const getUserRole = parsedPermissions.role;
        this.userRole = getUserRole;
        if (getUserId) {
          this.userId = getUserId;
        }
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
      }
    }
  }


  printPage(): void {
    const prevCollapsed = { ...this.collapsed };
    this.collapsed = {};
    this.cdr.detectChanges();

    const style = document.createElement('style');
    style.id = 'vc-print-override';
    style.textContent = `
      @media print {
        html, body {
          height: auto !important;
          min-height: 0 !important;
          overflow: visible !important;
          background: #fff !important;
        }
        app-root, app-simple { display: block !important; height: auto !important; min-height: 0 !important; }
        .vc-page { min-height: 0 !important; }
      }
    `;
    document.head.appendChild(style);

    setTimeout(() => {
      window.print();
      document.head.removeChild(style);
      this.collapsed = prevCollapsed;
      this.cdr.detectChanges();
    }, 80);
  }

  downloadPdf(): void {
    this.pdfService.generate(this.complaint, this.trackingList, this.canSeeInfo);
  }

  openAlert(message: string): void {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: "OK",
        },
      },
    });
  }


  preview(id: number): void {
    const doc = this.documentList.find(d => d.complaintDocumentId === id);
    if (!doc || !doc.complaintDocument) {
      this.openAlert("Document not found or invalid.");
      return;
    }
    this.dialog.open(AttachmentPreviewDialogComponent, {
      data: {
        documentName: doc.documentName,
        documentType: doc.documentType,
        complaintDocument: doc.complaintDocument,
      },
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'fullscreen-preview-dialog',
    });
  }

  private readonly INVALID_MOBILES = ['03000000000', '+923000000000'];
  private readonly INVALID_EMAILS = ['anonymous@pulse.gop.pk', 'anonymous@pulse.com'];

  isValidMobile(mobile: string | undefined | null): boolean {
    if (!mobile || mobile.trim() === '') return false;
    return !this.INVALID_MOBILES.includes(mobile.trim());
  }

  isValidEmail(email: string | undefined | null): boolean {
    if (!email || email.trim() === '') return false;
    return !this.INVALID_EMAILS.includes(email.trim().toLowerCase());
  }

  reload(): void {
    window.location.reload();
  }

  getStatusClass(status: string | undefined): string {
    if (!status) return 'vc-status-pill--closed';
    const s = status.toLowerCase();
    if (s.includes('closed') || s.includes('drop')) return 'vc-status-pill--closed';
    if (s.includes('resolved')) return 'vc-status-pill--resolved';
    if (s.includes('review')) return 'vc-status-pill--review';
    if (s.includes('process')) return 'vc-status-pill--in-process';
    return 'vc-status-pill--in-process';
  }

}
